import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { ButtonSpinner } from '../../components/ui/Loader';
import {
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetProductQuery,
} from '../../store/api/adminApi';
import { useGetAdminCategoriesQuery } from '../../store/api/adminApi';
import { useLanguage } from '../../hooks/useLanguage';

export function AdminProductFormPage() {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { productId } = useParams<{ productId: string }>();
  const isEditMode = !!productId;

  // Get product data if editing
  const { data: productData, isLoading: productLoading } = useGetProductQuery(
    productId || '',
    { skip: !isEditMode }
  );

  // Get all categories for selection
  const { data: categoriesData } = useGetAdminCategoriesQuery({ includeHidden: true });

  const [createProduct, { isLoading: isCreating }] = useCreateProductMutation();
  const [updateProduct, { isLoading: isUpdating }] = useUpdateProductMutation();

  // Form state
  const [formData, setFormData] = useState({
    categoryId: '',
    name: '',
    description: '',
    value: '',
    status: 'available' as 'available' | 'sold',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Load product data when editing
  useEffect(() => {
    if (isEditMode && productData?.data?.product) {
      const product = productData.data.product;
      const categoryId =
        typeof product.categoryId === 'object' && product.categoryId != null
          ? product.categoryId._id
          : product.categoryId ?? '';
      
      setFormData({
        categoryId: categoryId || '',
        name: product.name || '',
        description: product.description || '',
        value: product.value || '',
        status: product.status || 'available',
      });
    }
  }, [isEditMode, productData]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.categoryId.trim()) {
      newErrors.categoryId = t('admin.product.form.errors.categoryRequired');
    }
    if (!formData.name.trim()) {
      newErrors.name = t('admin.product.form.errors.nameRequired');
    }
    if (!formData.value.trim()) {
      newErrors.value = t('admin.product.form.errors.valueRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t('admin.product.form.errors.validationFailed'));
      return;
    }

    try {
      if (isEditMode && productId) {
        const updateData: {
          name?: string;
          description?: string;
          value?: string;
          status?: 'available' | 'sold';
        } = {
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          value: formData.value.trim(),
          status: formData.status,
        };

        await updateProduct({ productId, data: updateData }).unwrap();
        toast.success(t('admin.product.form.updateSuccess'));
      } else {
        const submitData = {
          categoryId: formData.categoryId.trim(),
          name: formData.name.trim(),
          description: formData.description.trim() || undefined,
          value: formData.value.trim(),
        };

        await createProduct(submitData).unwrap();
        toast.success(t('admin.product.form.createSuccess'));
      }

      navigate('/admin/products');
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      toast.error(
        error?.data?.message || error?.message || t('admin.product.form.submitError')
      );
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  if (isEditMode && productLoading) {
    return (
      <div className="min-h-screen">
        <div className="text-center py-16">
          <p className="text-neutral-medium text-xl font-body animate-pulse">
            {t('common.loading')}
          </p>
        </div>
      </div>
    );
  }

  const categories = categoriesData?.data.categories || [];

  return (
    <div className="min-h-screen animate-slide-up">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-brand-accent mb-2">
          {isEditMode ? t('admin.product.form.editTitle') : t('admin.product.form.createTitle')}
        </h1>
        <p className="text-neutral-medium text-lg font-body">
          {isEditMode
            ? t('admin.product.form.editSubtitle')
            : t('admin.product.form.createSubtitle')}
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card-glass rounded-2xl shadow-2xl p-6 md:p-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.product.form.category')} <span className="text-danger">*</span>
            </label>
            <select
              name="categoryId"
              value={formData.categoryId}
              onChange={handleChange}
              disabled={isEditMode}
              className={`w-full px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent disabled:opacity-50 disabled:cursor-not-allowed ${
                errors.categoryId ? 'border-danger' : 'border-neutral-dark'
              }`}
            >
              <option value="">{t('admin.product.form.selectCategory')}</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {currentLanguage === 'ar' ? cat.name : cat.nameEn}
                </option>
              ))}
            </select>
            {errors.categoryId && (
              <p className="mt-1 text-sm text-danger font-body">{errors.categoryId}</p>
            )}
            {isEditMode && (
              <p className="mt-1 text-xs text-neutral-medium font-body">
                {t('admin.product.form.categoryNote')}
              </p>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.product.form.name')} <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className={`w-full px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent ${
                errors.name ? 'border-danger' : 'border-neutral-dark'
              }`}
              placeholder={t('admin.product.form.namePlaceholder')}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-danger font-body">{errors.name}</p>
            )}
          </div>

          {/* Description */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.product.form.description')}
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent resize-none"
              placeholder={t('admin.product.form.descriptionPlaceholder')}
            />
          </div>

          {/* Value - textarea لدعم السطور الجديدة وإرسالها كما هي */}
          <div>
            <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
              {t('admin.product.form.value')} <span className="text-danger">*</span>
            </label>
            <textarea
              name="value"
              value={formData.value}
              onChange={handleChange}
              rows={5}
              className={`w-full px-4 py-2 bg-neutral-dark border rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent font-mono resize-y ${
                errors.value ? 'border-danger' : 'border-neutral-dark'
              }`}
              placeholder={t('admin.product.form.valuePlaceholder')}
            />
            {errors.value && (
              <p className="mt-1 text-sm text-danger font-body">{errors.value}</p>
            )}
            <p className="mt-1 text-xs text-neutral-medium font-body">
              {t('admin.product.form.valueNote')}
            </p>
          </div>

          {/* Status (only in edit mode) */}
          {isEditMode && (
            <div>
              <label className="block text-sm font-medium text-neutral-medium mb-2 font-body">
                {t('admin.product.form.status')}
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body focus:outline-none focus:border-brand-accent"
              >
                <option value="available">{t('admin.products.status.available')}</option>
                <option value="sold">{t('admin.products.status.sold')}</option>
              </select>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mt-8 pt-6 border-t border-neutral-dark">
          <button
            type="submit"
            disabled={isCreating || isUpdating}
            className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center justify-center gap-2"
          >
            {isCreating || isUpdating ? (
              <>
                <ButtonSpinner />
                <span>{t('common.loading')}</span>
              </>
            ) : isEditMode ? (
              t('admin.product.form.updateButton')
            ) : (
              t('admin.product.form.createButton')
            )}
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light font-heading font-semibold rounded-lg hover:bg-neutral-dark/80 transform hover:scale-105 transition-all duration-300"
          >
            {t('common.cancel')}
          </button>
        </div>
      </form>
    </div>
  );
}

